/**
 * NetworkManager - Manages P2P connections via PeerJS
 * Provides room-based multiplayer with host authority model
 */

import Peer, { DataConnection } from 'peerjs';
import type { INetworkProvider, MessageHandler, NetworkMessage } from './INetworkProvider';
import { validatePayload } from './NetworkSchemas';

// Re-export types from interface for backward compatibility
export type { NetworkMessage, MessageHandler } from './INetworkProvider';

export interface PlayerState {
    id: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    health: number;
    capacity: number;
    color: number;
    isCharging?: boolean; // Sync charging state with position updates
    isStunned?: boolean;  // Sync stun state
}

export const SessionMessages = {
    ROUND_START: 'SESSION_ROUND_START',
    ROUND_END: 'SESSION_ROUND_END',
    PLAYER_READY: 'SESSION_PLAYER_READY',
    PLAYER_LEFT: 'SESSION_PLAYER_LEFT',
} as const;

export interface LobbyPlayer {
    id: string;
    name: string;
    isReady: boolean;
    isHost: boolean;
}


// High-frequency messages that can be skipped in performance mode
const HIGH_FREQUENCY_MESSAGES = ['PLAYER_POSITION', 'REMOTE_STATES_SYNC'];

// ICE servers for WebRTC NAT traversal
// STUN helps discover public IP, TURN relays traffic when direct P2P fails
// Using metered.ca TURN server for reliable mobile/cross-network connectivity
// For production: use the API endpoint to get rotating credentials:
//   https://farts.metered.live/api/v1/turn/credentials?apiKey=<API_KEY>
const ICE_SERVERS: RTCIceServer[] = [
    { urls: 'stun:stun.relay.metered.ca:80' },
    {
        urls: 'turn:standard.relay.metered.ca:80',
        username: '42b6e22d1a154e202d8af6ba',
        credential: '02BPw+QlVqm7ohVb',
    },
    {
        urls: 'turn:standard.relay.metered.ca:80?transport=tcp',
        username: '42b6e22d1a154e202d8af6ba',
        credential: '02BPw+QlVqm7ohVb',
    },
    {
        urls: 'turn:standard.relay.metered.ca:443',
        username: '42b6e22d1a154e202d8af6ba',
        credential: '02BPw+QlVqm7ohVb',
    },
    {
        urls: 'turns:standard.relay.metered.ca:443?transport=tcp',
        username: '42b6e22d1a154e202d8af6ba',
        credential: '02BPw+QlVqm7ohVb',
    },
];

export class NetworkManager implements INetworkProvider {
    private peer: Peer | null = null;
    private connections: Map<string, DataConnection> = new Map();
    private messageHandlers: Map<string, MessageHandler[]> = new Map();
    private _isHost: boolean = false;
    private _roomCode: string = '';
    private _playerId: string = '';
    private _connected: boolean = false;
    private debugPlayerNum: number | null = null;
    private msgCounter = 0;
    private performanceMode: boolean = false; // Skip logging high-frequency messages

    // Callbacks
    public onPeerJoin?: (peerId: string) => void;
    public onPeerLeave?: (peerId: string) => void;
    public onConnectionReady?: () => void;
    public onError?: (error: Error) => void;

    constructor() {
        // Check if we're in debug mode inside an iframe
        const params = new URLSearchParams(window.location.search);
        const debugPlayer = params.get('debugPlayer');
        if (debugPlayer && window.parent !== window) {
            this.debugPlayerNum = parseInt(debugPlayer);

            // Listen for performance mode toggle from parent debug window
            window.addEventListener('message', (event) => {
                if (event.data?.type === 'DEBUG_PERFORMANCE_MODE') {
                    this.performanceMode = event.data.enabled;
                }
            });
        }
    }

    /**
     * Log network message to debug terminal (if in debug mode)
     * Skips high-frequency messages when performance mode is enabled
     */
    private logToDebugTerminal(direction: 'IN' | 'OUT', type: string, payload: unknown, id?: string): void {
        if (!this.debugPlayerNum || window.parent === window) return;

        // Skip high-frequency messages in performance mode
        if (this.performanceMode && HIGH_FREQUENCY_MESSAGES.includes(type)) return;

        window.parent.postMessage({
            type: 'DEBUG_NETWORK',
            playerNum: this.debugPlayerNum,
            direction,
            messageType: type,
            payload,
            id // Pass tracing ID to debug terminal
        }, '*');
    }

    get isHost(): boolean { return this._isHost; }
    get roomCode(): string { return this._roomCode; }
    get playerId(): string { return this._playerId; }
    get isConnected(): boolean { return this._connected; }
    get peerIds(): string[] { return Array.from(this.connections.keys()); }

    /**
     * Generate a random room code
     */
    private generateRoomCode(): string {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing characters
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    }

    /**
     * Create a new room as host
     */
    async createRoom(playerName: string = 'Host', forcedRoomCode?: string): Promise<string> {
        this._roomCode = forcedRoomCode ? forcedRoomCode.toUpperCase() : this.generateRoomCode();
        this._isHost = true;

        return new Promise((resolve, reject) => {
            // Use room code as peer ID for easy joining
            this.peer = new Peer(`fart-${this._roomCode}`, {
                debug: 0,
                config: {
                    iceServers: ICE_SERVERS,
                },
            });

            this.peer.on('open', (id) => {
                this._playerId = id;
                this._connected = true;
                this.onConnectionReady?.();
                resolve(this._roomCode);
            });

            this.peer.on('connection', (conn) => {
                this.handleIncomingConnection(conn);
            });

            this.peer.on('error', (err) => {
                console.error('[NetworkManager] PeerJS error:', err);
                this.onError?.(err);
                reject(err);
            });

            this.peer.on('disconnected', () => {
                this._connected = false;
            });
        });
    }

    /**
     * Join an existing room
     */
    async joinRoom(roomCode: string, playerName: string = 'Player'): Promise<void> {
        this._roomCode = roomCode.toUpperCase();
        this._isHost = false;

        return new Promise((resolve, reject) => {
            // Generate unique peer ID
            const uniqueId = `fart-${this._roomCode}-${Date.now().toString(36)}`;

            this.peer = new Peer(uniqueId, {
                debug: 0,
                config: {
                    iceServers: ICE_SERVERS,
                },
            });

            this.peer.on('open', (id) => {
                this._playerId = id;
                this._connected = true;

                // Connect to host
                const hostPeerId = `fart-${this._roomCode}`;

                const conn = this.peer!.connect(hostPeerId, {
                    reliable: true,
                });

                conn.on('open', () => {
                    this.connections.set(hostPeerId, conn);
                    this.setupConnectionHandlers(conn);
                    this.onConnectionReady?.();
                    resolve();
                });

                conn.on('error', (err) => {
                    console.error('[NetworkManager] Connection error:', err);
                    reject(err);
                });
            });

            this.peer.on('connection', (conn) => {
                // Handle connections from other peers (for mesh network)
                this.handleIncomingConnection(conn);
            });

            this.peer.on('error', (err) => {
                console.error('[NetworkManager] PeerJS error:', err);
                if (err.type === 'peer-unavailable') {
                    reject(new Error(`Room "${roomCode}" not found`));
                } else {
                    this.onError?.(err);
                    reject(err);
                }
            });
        });
    }

    /**
     * Handle an incoming peer connection
     */
    private handleIncomingConnection(conn: DataConnection): void {
        conn.on('open', () => {
            this.connections.set(conn.peer, conn);
            this.setupConnectionHandlers(conn);
            this.onPeerJoin?.(conn.peer);
        });
    }

    /**
     * Set up message handlers for a connection
     */
    private setupConnectionHandlers(conn: DataConnection): void {
        conn.on('data', (data) => {
            const message = data as NetworkMessage;
            this.handleMessage(message, conn.peer);
        });

        conn.on('close', () => {
            this.connections.delete(conn.peer);
            this.onPeerLeave?.(conn.peer);
        });

        conn.on('error', (err) => {
            console.error(`[NetworkManager] Connection error with ${conn.peer}:`, err);
        });
    }

    /**
     * Handle incoming message
     */
    private handleMessage(message: NetworkMessage, senderId: string): void {
        // Log to debug terminal
        this.logToDebugTerminal('IN', message.type, message.payload, message.id);

        // Validate payload in dev mode (warnings only, doesn't block)
        validatePayload(message.type, message.payload);

        const handlers = this.messageHandlers.get(message.type);
        if (handlers) {
            handlers.forEach(handler => handler(message, senderId));
        }

        // Also call "any" handlers
        const anyHandlers = this.messageHandlers.get('*');
        if (anyHandlers) {
            anyHandlers.forEach(handler => handler(message, senderId));
        }
    }

    /**
     * Subscribe to messages of a specific type
     */
    subscribe(messageType: string, handler: MessageHandler): void {
        if (!this.messageHandlers.has(messageType)) {
            this.messageHandlers.set(messageType, []);
        }
        this.messageHandlers.get(messageType)!.push(handler);
    }

    /**
     * Unsubscribe from messages of a specific type
     */
    unsubscribe(messageType: string): void {
        this.messageHandlers.delete(messageType);
    }

    /**
     * Send message to a specific peer
     */
    send(peerId: string, type: string, payload: unknown): void {
        const conn = this.connections.get(peerId);
        if (conn && conn.open) {
            const id = `${this._playerId}-${Date.now()}-${this.msgCounter++}`;

            // Log to debug terminal
            this.logToDebugTerminal('OUT', type, payload, id);

            const message: NetworkMessage = {
                id,
                type,
                payload,
                senderId: this._playerId,
                timestamp: Date.now(),
            };
            conn.send(message);
        }
    }

    /**
     * Broadcast message to all connected peers
     */
    broadcast(type: string, payload: unknown): void {
        const id = `${this._playerId}-${Date.now()}-${this.msgCounter++}`;

        // Log to debug terminal
        this.logToDebugTerminal('OUT', type, payload, id);

        const message: NetworkMessage = {
            id,
            type,
            payload,
            senderId: this._playerId,
            timestamp: Date.now(),
        };

        this.connections.forEach((conn) => {
            if (conn.open) {
                conn.send(message);
            }
        });
    }



    /**
     * Disconnect and cleanup
     */
    disconnect(): void {
        this.connections.forEach((conn) => {
            conn.close();
        });
        this.connections.clear();

        if (this.peer) {
            this.peer.destroy();
            this.peer = null;
        }

        this._connected = false;
        this._isHost = false;
        this._roomCode = '';
        this.messageHandlers.clear();
    }
}

// Singleton instance
export const networkManager = new NetworkManager();
